import { difference, isEmpty, upperFirst, min, max } from 'lodash';

import { ApprovedStatus, ApprovedStatusLabel, MULTIPLE_LABEL } from '../constants/approvedStatus';
import {
  ALL_PRICE_TYPES,
  IMAGE_COLUMN,
  MODEL_COLUMN,
  MSRP_PRICE_COLUMN,
  PRICING_VARIANT_IDENTIFIERS,
  RETAIL_PRICE_COLUMN,
  SALE_PRICE_COLUMN,
  STATUS_COLUMN,
  UNIT_PRICE_COLUMN,
  RETAILER_PRODUCTS_CURRENCY_LEVELS,
} from '../constants/pricings';
import {
  PRICING_FILTER_CHANNELS_KEY,
  PRICING_FILTER_CURRENCIES_KEY,
  PRICING_FILTER_HAS_PRICE_KEY,
  PRICING_FILTER_SEARCH_KEY,
  PRICING_FILTER_STATUSES_KEY,
} from '../store/actions/pricing';
import { differenceInMinutes } from 'date-fns';

export function getVariantStatus(permissions, retailerIds) {
  if (!permissions) return ApprovedStatusLabel[ApprovedStatus.NOT_APPROVED];

  const filteredPermissions = retailerIds?.length
    ? permissions.filter((permission) => retailerIds.includes(permission.retailer_id))
    : permissions;

  if (!filteredPermissions.length) return ApprovedStatusLabel[ApprovedStatus.NOT_APPROVED];

  const hasNotApproved =
    filteredPermissions.some((permission) => !permission.ecommerce_approved) ||
    retailerIds.some((retailerId) => permissions.some((permission) => permission.retailer_id === retailerId));
  const hasPending = filteredPermissions.some(
    (permission) => permission.ecommerce_approved && !permission.ecommerce_approved_by_retailer,
  );
  const hasSynced = filteredPermissions.some(
    (permission) => permission.ecommerce_approved && permission.ecommerce_approved_by_retailer,
  );

  if (retailerIds.length > 1) {
    const hasMultiple = (hasNotApproved && (hasPending || hasSynced)) || (hasPending && hasSynced);
    if (hasMultiple) return MULTIPLE_LABEL;
  }

  if (hasPending) return ApprovedStatusLabel[ApprovedStatus.PENDING];
  if (hasSynced) return ApprovedStatusLabel[ApprovedStatus.SYNCED];
  return ApprovedStatusLabel[ApprovedStatus.NOT_APPROVED];
}

export function getVariantPrices(prices) {
  if (!prices) return { hasPrices: false, channels: [] };

  const channels = new Set();
  const pricesByType = {};

  Object.keys(prices).forEach((currency) => {
    pricesByType[currency] = pricesByType[currency] || {};
    prices[currency].forEach((price) => {
      if (price.type === MSRP_PRICE_COLUMN) return;

      pricesByType[currency][price.type] = price.amount / 100;
      if (price.type === SALE_PRICE_COLUMN) {
        pricesByType[currency].sale_start = price.start_date;
        pricesByType[currency].sale_end = price.end_date;
      }
      channels.add(price.retailer_id);
    });
  });

  return {
    hasPrices: !isEmpty(pricesByType),
    hasMultipleCurrencies: Object.keys(pricesByType).length > 1,
    prices: pricesByType,
    channels: Array.from(channels),
  };
}

export function getVariantPricesByChannel(prices) {
  const channels = new Set();
  const pricesByType = {};

  if (!prices) return { hasPrices: false, channels: null };

  Object.keys(prices).forEach((currency) => {
    pricesByType[currency] = pricesByType[currency] || {};
    prices[currency].forEach((price) => {
      if (price.type === MSRP_PRICE_COLUMN) return;

      pricesByType[currency][price.retailer_id] = pricesByType[currency][price.retailer_id] || {};
      pricesByType[currency][price.retailer_id][price.type] = price.amount / 100;
      if (price.type === SALE_PRICE_COLUMN) {
        pricesByType[currency][price.retailer_id].sale_start = price.start_date;
        pricesByType[currency][price.retailer_id].sale_end = price.end_date;
      }
      channels.add(price.retailer_id);
    });
  });

  return {
    hasPrices: !isEmpty(pricesByType),
    hasMultipleCurrencies: Object.keys(pricesByType).length > 1,
    prices: pricesByType,
    channels: Array.from(channels),
  };
}

export function getAvailablePriceTypes(variants) {
  const priceTypes = variants.reduce((prices, variant) => {
    if (!variant.prices) return prices;

    Object.keys(variant.prices).forEach((currency) => {
      variant.prices[currency].forEach((price) => {
        if (price.type !== MSRP_PRICE_COLUMN) {
          prices.add(price.type);
        }
      });
    });

    return prices;
  }, new Set());

  const priceTypeArray = [...priceTypes];
  priceTypeArray.sort((priceTypeA, priceTypeB) => {
    if (priceTypeA === RETAIL_PRICE_COLUMN || priceTypeB === SALE_PRICE_COLUMN) return -1;
    if (priceTypeB === RETAIL_PRICE_COLUMN || priceTypeA === SALE_PRICE_COLUMN) return 1;
    return priceTypeA - priceTypeB;
  });

  return priceTypeArray;
}

export function getAvailableTableColumns(hiddenColumns) {
  const modelIsShown = !hiddenColumns.includes(MODEL_COLUMN);
  const hiddenIdentifiers = PRICING_VARIANT_IDENTIFIERS.filter((identifier) => hiddenColumns.includes(identifier));
  const disableOtherIdentifiers =
    hiddenIdentifiers.length + (modelIsShown ? 0 : 1) === PRICING_VARIANT_IDENTIFIERS.length;

  const columns = [
    { value: IMAGE_COLUMN, label: 'Image' },
    { value: STATUS_COLUMN, label: 'Status' },
    { separator: true },
  ];

  columns.push({ value: MODEL_COLUMN, label: 'Model', disabled: disableOtherIdentifiers && modelIsShown });
  PRICING_VARIANT_IDENTIFIERS.forEach((identifier) => {
    columns.push({
      value: identifier,
      label: identifier.toUpperCase(),
      disabled: disableOtherIdentifiers && !hiddenIdentifiers.includes(identifier),
    });
  });

  const hiddenPriceTypes = ALL_PRICE_TYPES.filter((priceType) => hiddenColumns.includes(priceType));
  const disableOtherPriceTypes = hiddenPriceTypes.length === ALL_PRICE_TYPES.length - 1;
  columns.push({ separator: true });
  ALL_PRICE_TYPES.forEach((priceType) => {
    columns.push({
      value: priceType,
      label: `${upperFirst(priceType)} price`,
      disabled: disableOtherPriceTypes && !hiddenPriceTypes.includes(priceType),
    });
  });

  return columns;
}

export function isEmptyFilter({ key, value, pricingCurrencies, pricingChannels }) {
  switch (key) {
    case PRICING_FILTER_STATUSES_KEY:
      return value.length === Object.values(ApprovedStatus).length - 1;
    case PRICING_FILTER_CURRENCIES_KEY:
      return pricingCurrencies.length === 0 || value.length === (pricingCurrencies ?? []).length;
    case PRICING_FILTER_CHANNELS_KEY:
      return value.length === (pricingChannels ?? []).length;
    case PRICING_FILTER_HAS_PRICE_KEY:
      return !value;
    default:
      return value.length === 0;
  }
}

export const getActiveFilter = ({ filters, availableOptions }) => {
  if (difference(availableOptions?.currencies || [], filters.currencies).length > 0) {
    return PRICING_FILTER_CURRENCIES_KEY;
  }

  const channelDiff = difference(availableOptions?.channels?.map((channel) => channel.id) || [], filters.channels);
  if (channelDiff.length > 0) {
    return PRICING_FILTER_CHANNELS_KEY;
  }

  if (filters.search?.length > 0) {
    return PRICING_FILTER_SEARCH_KEY;
  }

  const statusesDiff = difference(availableOptions?.statuses?.map((status) => status.value) || [], filters.statuses);
  if (statusesDiff.length > 0) {
    return PRICING_FILTER_STATUSES_KEY;
  }

  if (filters.hasPrice) {
    return PRICING_FILTER_HAS_PRICE_KEY;
  }

  return null;
};

export function getRetailerPricesWithRange(allPrices) {
  const pricesKeys = Object.keys(allPrices);
  if (pricesKeys.length === 1) {
    return allPrices[pricesKeys[0]];
  }

  const allRetailerPrices = Object.values(allPrices).reduce((retailerPrices, price) => {
    Object.keys(price).forEach((priceType) => {
      if (!retailerPrices[priceType]) {
        retailerPrices[priceType] = new Set();
      }
      retailerPrices[priceType].add(price[priceType]);
    });
    return retailerPrices;
  }, {});

  return Object.entries(allRetailerPrices).reduce((retailerPrices, [priceType, prices]) => {
    const hasMissingPrices = Object.values(allPrices).some(
      (retailerPrices) => !retailerPrices.hasOwnProperty(priceType),
    );

    if (hasMissingPrices) {
      retailerPrices[priceType] = MULTIPLE_LABEL;
    } else {
      const sortedPrices = [...prices].sort((a, b) => a - b);
      const [firstSortedPrice] = sortedPrices;
      const lastSortedPrice = sortedPrices[sortedPrices.length - 1];

      if (sortedPrices.length === 1) {
        retailerPrices[priceType] = firstSortedPrice;
      } else if (priceType === 'sale_start' || priceType === 'sale_end') {
        retailerPrices[priceType] =
          differenceInMinutes(firstSortedPrice, lastSortedPrice) > 1
            ? `${firstSortedPrice} - ${lastSortedPrice}`
            : firstSortedPrice;
      } else {
        retailerPrices[priceType] = `${firstSortedPrice} - ${lastSortedPrice}`;
      }
    }
    return retailerPrices;
  }, {});
}

export function doesVariantHasSalePrice(variant) {
  if (!variant.prices) return false;
  return Object.values(variant.prices)
    .flatMap((price) => price.type)
    .some(isPriceTypeSale);
}

export function getMandatoryCurrencyFromRetailer(retailer = {}) {
  const { currencies = [] } = retailer?.ecommerce_products_integration?.pricing || {};
  if (!currencies?.length) return null;

  const mandatoryCurrency = currencies.find(
    (currency) => currency.level === RETAILER_PRODUCTS_CURRENCY_LEVELS.MANDATORY,
  );

  if (!mandatoryCurrency) {
    console.warn('No mandatory currency found for retailer, using first currency', retailer.id);
    return currencies[0].currency;
  }

  return mandatoryCurrency.currency;
}

const isPriceTypeSale = (priceType) => priceType === SALE_PRICE_COLUMN;

export function enrichVariantsWithPriceDataForRetailerProductsListUi({ variants, retailerMandatoryCurrency }) {
  return variants.map((variant) => ({
    ...variant,
    priceData: getVariantPriceDataForRetailerProductsListUi({ variant, retailerMandatoryCurrency }),
  }));
}

export function getProductPriceRangeForRetailerProductsListUi({ variants = [], retailerMandatoryCurrency }) {
  if (isEmpty(variants)) return null;

  // Collect all price data from variants that have active prices in the retailer's currency
  const priceDataList = variants
    .map((variant) => getVariantPriceDataForRetailerProductsListUi({ variant, retailerMandatoryCurrency }))
    .filter(Boolean);

  if (isEmpty(priceDataList)) return null;

  // Extract all valid prices for each type
  const retailPrices = priceDataList.map((pd) => pd.retail).filter(Number.isFinite);
  const unitPrices = priceDataList.map((pd) => pd.unit).filter(Number.isFinite);

  const hasMultipleVariants = variants.length > 1;

  return {
    retail: hasMultipleVariants ? null : retailPrices[0],
    unit: hasMultipleVariants ? null : unitPrices[0],
    range: hasMultipleVariants
      ? {
          retail: retailPrices.length ? { min: min(retailPrices), max: max(retailPrices) } : null,
          unit: unitPrices.length ? { min: min(unitPrices), max: max(unitPrices) } : null,
        }
      : null,
    currency: retailerMandatoryCurrency,
  };
}

export function getVariantPriceDataForRetailerProductsListUi({ variant, retailerMandatoryCurrency }) {
  if (isEmpty(variant.prices)) return null;

  const relevantPrices = variant.prices?.[retailerMandatoryCurrency]?.filter((price) => price.is_active) || [];

  const priceRange = {
    retail: getVariantPriceRangeForRetailerProductsListUi({ prices: relevantPrices, priceType: RETAIL_PRICE_COLUMN }),
    unit: getVariantPriceRangeForRetailerProductsListUi({ prices: relevantPrices, priceType: UNIT_PRICE_COLUMN }),
  };

  return {
    retail: relevantPrices.find((price) => price.type === RETAIL_PRICE_COLUMN)?.amount,
    unit: relevantPrices.find((price) => price.type === UNIT_PRICE_COLUMN)?.amount,
    range: priceRange,
    currency: retailerMandatoryCurrency,
  };
}

function getVariantPriceRangeForRetailerProductsListUi({ prices, priceType }) {
  if (isEmpty(prices)) return null;
  if (prices.length <= 1) return null;

  const relevantPrices = prices.filter((price) => price.type === priceType);
  if (relevantPrices.length <= 1) return null;

  return {
    min: min(relevantPrices.map((price) => price.amount)),
    max: max(relevantPrices.map((price) => price.amount)),
  };
}
